// ---------------------------------------------------------------------------
// Page registry — maps route keys to page definitions
// ---------------------------------------------------------------------------
//
// Keys use full path without leading slash (e.g. 'content/pages').
// Must match router's pageKey convention.

/**
 * Create a page registry.
 * @returns {import('../../contracts/page.js').PageRegistry}
 */
export function createPageRegistry() {
  /** @type {Map<string, import('../../contracts/page.js').PageDef>} */
  const pages = new Map();

  /**
   * @param {import('../../contracts/page.js').PageDef} pageDef
   */
  function register(pageDef) {
    const { key } = pageDef;

    // Validate key format
    if (!key || key.length === 0) {
      throw new Error('Page key must not be empty (root is reserved for default-route navigation)');
    }
    if (key.startsWith('/')) {
      throw new Error(`Page key must not start with '/': got '${key}'`);
    }
    if (key.endsWith('/')) {
      throw new Error(`Page key must not end with '/': got '${key}'`);
    }
    if (key.includes('?') || key.includes('#')) {
      throw new Error(`Page key must not contain '?' or '#': got '${key}'`);
    }

    // Validate mount/loader XOR (union-variant model)
    const hasMount = 'mount' in pageDef && typeof pageDef.mount === 'function';
    const hasLoader = 'loader' in pageDef && typeof pageDef.loader === 'function';

    if (hasMount && hasLoader) {
      throw new Error(`Page '${key}': provide either 'mount' or 'loader', not both`);
    }
    if (!hasMount && !hasLoader) {
      throw new Error(`Page '${key}': must provide either 'mount' (StaticPageDef) or 'loader' (LazyPageDef)`);
    }

    // Duplicate check
    if (pages.has(key)) {
      throw new Error(`Page '${key}' is already registered`);
    }

    pages.set(key, pageDef);
  }

  /**
   * @param {string} key
   * @returns {import('../../contracts/page.js').PageDef | undefined}
   */
  function get(key) {
    return pages.get(key);
  }

  /**
   * @returns {import('../../contracts/page.js').PageDef[]}
   */
  function list() {
    return [...pages.values()];
  }

  return { register, get, list };
}
